import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Open/close behaviour for a pill dropdown: click outside to dismiss, Escape to dismiss.
 *
 * Behaviour only, no markup — the hole viewer's pickers sit on dark chrome and the stats
 * filters on a light surface, so they share nothing visually. What they must not do is grow
 * two copies of the dismissal logic, because that is the half that silently rots (a listener
 * that is never removed, an Escape handler only one of them has).
 */
export function useMenu<T extends HTMLElement = HTMLDivElement>(): {
  open: boolean;
  toggle: () => void;
  close: () => void;
  ref: RefObject<T | null>;
} {
  const [open, setOpen] = useState(false);
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return {
    open,
    toggle: () => setOpen((v) => !v),
    close: () => setOpen(false),
    ref,
  };
}
